// Live implementation of the DataSource seam: the public widget API client.
//
// Hits GET /widget/{token}/config and GET /widget/{token}/locations (server
// task-030). No auth, no cookies, no storage (GDPR cookieless) — the token is
// the only credential and it travels in the path. Responses are plain JSON; we
// deliberately do NOT send credentials so the browser can serve these from the
// CDN edge cache the server is designed around (F-064).

import { DataSource, DataSourceError } from "./DataSource";
import type { AutocompletePayload, ConfigPayload, LocationDetail, ViewportPayload, ViewportQuery } from "./types";

export class LiveDataSource implements DataSource {
  /**
   * @param token the LOCATOR public_token (path credential).
   * @param apiBase origin serving the widget API (Storadar's origin, NOT the
   *   customer page's). Defaults to the widget script's own origin (self-located
   *   from import.meta.url in StoradarMap), so the embed snippet needs an
   *   explicit api-base ONLY when the script is served from a dedicated CDN whose
   *   origin differs from the API's. Empty string means same-origin/root-relative.
   */
  constructor(
    private readonly token: string,
    private readonly apiBase: string,
  ) {}

  async loadConfig(): Promise<ConfigPayload> {
    return this.getJson<ConfigPayload>(`${this.base()}/widget/${encodeURIComponent(this.token)}/config`);
  }

  async loadViewport(query: ViewportQuery, signal?: AbortSignal): Promise<ViewportPayload> {
    const url = `${this.base()}/widget/${encodeURIComponent(this.token)}/locations?${this.viewportParams(query)}`;
    return this.getJson<ViewportPayload>(url, signal);
  }

  async loadAutocomplete(query: string, signal?: AbortSignal): Promise<AutocompletePayload> {
    const params = new URLSearchParams({ q: query });
    const url = `${this.base()}/widget/${encodeURIComponent(this.token)}/autocomplete?${params.toString()}`;
    return this.getJson<AutocompletePayload>(url, signal);
  }

  async loadDetail(id: string, signal?: AbortSignal): Promise<LocationDetail> {
    const url = `${this.base()}/widget/${encodeURIComponent(this.token)}/location/${encodeURIComponent(id)}`;
    return this.getJson<LocationDetail>(url, signal);
  }

  /** Trailing slash stripped so path concatenation never double-slashes. */
  private base(): string {
    return this.apiBase.replace(/\/+$/, "");
  }

  /**
   * Builds the `/locations` query string in the exact shape the server's
   * ViewportRequest.parse expects: `bbox=w,s,e,n`, comma-joined `tags`, and the
   * snake_case center/page params. Absent optionals are omitted, not sent empty.
   */
  private viewportParams(query: ViewportQuery): string {
    const { bbox } = query;
    const params = new URLSearchParams();
    params.set("bbox", `${bbox.west},${bbox.south},${bbox.east},${bbox.north}`);
    params.set("zoom", String(query.zoom));
    if (query.tags.length > 0) {
      params.set("tags", query.tags.join(","));
    }
    if (query.openNow) {
      params.set("open_now", "true");
    }
    if (query.center) {
      params.set("center_lat", String(query.center.lat));
      params.set("center_lng", String(query.center.lng));
    }
    // Only sent when the camera actually needs the result extent (F-039) — it is an expensive
    // server-side aggregate, so an absent param must mean "don't compute it".
    if (query.bounds) {
      params.set("bounds", "1");
    }
    params.set("page", String(query.page));
    if (query.pageSize != null) {
      params.set("page_size", String(query.pageSize));
    }
    return params.toString();
  }

  /**
   * Single fetch path with honest error mapping. A network throw or a 5xx is
   * retryable; a 404 (unknown/rotated token) or 400 (bad request) is not — the
   * component uses [DataSourceError.retryable] to decide whether to show a retry
   * button or a terminal state. We never swallow a failure into an empty result.
   */
  private async getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
    let response: Response;
    try {
      // No credentials: keeps responses CDN-cacheable and sends no cookies (GDPR).
      response = await fetch(url, { credentials: "omit", signal });
    } catch (cause) {
      // AbortError is a normal cancellation, not a failure — rethrow as-is so the
      // component can ignore it rather than showing an error state.
      if (cause instanceof DOMException && cause.name === "AbortError") {
        throw cause;
      }
      throw new DataSourceError("Could not reach the store locator service.", true);
    }
    if (!response.ok) {
      // 429 (rate-limited, F-063) and 5xx are retryable; 4xx like 404/400 are not.
      // Honor Retry-After on a 429 so the retry waits out the limiter.
      if (response.status === 429) {
        throw new DataSourceError(
          "The store locator is busy right now.",
          true,
          parseRetryAfter(response.headers.get("Retry-After")),
        );
      }
      const retryable = response.status >= 500;
      throw new DataSourceError(`Store locator request failed (${response.status}).`, retryable);
    }
    try {
      return (await response.json()) as T;
    } catch {
      throw new DataSourceError("The store locator returned an unreadable response.", true);
    }
  }
}

/**
 * Parses a `Retry-After` header (RFC 7231): either delta-seconds or an HTTP-date.
 * Returns whole seconds to wait (>= 0), or null when absent/unparseable. The
 * server emits delta-seconds, but the HTTP-date form is handled for correctness.
 */
function parseRetryAfter(header: string | null): number | null {
  if (!header) {
    return null;
  }
  const seconds = Number(header);
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.round(seconds));
  }
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    return Math.max(0, Math.round((date - Date.now()) / 1000));
  }
  return null;
}
